import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildInfo } from '../config/buildInfo';
import { getSiteName } from '../config/appConfig';
import { useSnackbar } from 'notistack';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from?.pathname || '/menu-principal';

  useEffect(() => {
    const message = (location.state as any)?.message;
    if (message) {
      enqueueSnackbar(message, { variant: 'success' });
    }

    const authRedirectReason = sessionStorage.getItem('authRedirectReason');
    if (authRedirectReason === 'session_expired') {
      enqueueSnackbar('Tu sesion expiro. Inicia sesion nuevamente.', { variant: 'warning' });
      sessionStorage.removeItem('authRedirectReason');
    }
  }, [location.state, enqueueSnackbar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Credenciales inválidas');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src="/assets/eone-logo.png"
            alt="eOne"
            sx={{ height: 80, width: 'auto', mb: 2 }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
            }}
          >
            {getSiteName()}
          </Typography>
          <Typography component="h2" variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 400 }}>
            Iniciar Sesión
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Usuario o email"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              helperText="Podés iniciar sesión con tu ID de usuario o con tu email"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'ocultar contraseña' : 'mostrar contraseña'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Iniciar Sesión
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  fontSize: '0.875rem'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Link>
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              color: 'text.secondary',
              fontSize: '0.7rem',
              opacity: 0.6,
            }}
          >
            v{buildInfo.version} Build {buildInfo.buildNumber}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 